/**
 * HomeworkResourcesBlock — downloadable scaffold/dataset for a module's homework.
 *
 * Injected at the homework heading. Lists the module's resource files (from
 * GET /modules/{slug}/resources) and downloads each via an authenticated blob
 * fetch — so the homework's "provided with this module" reference resolves to a
 * real, tier-gated download instead of a dangling link.
 *
 * Renders nothing when the module has no resources (or is locked).
 */

import React, { useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ModuleResources,
  downloadModuleResource,
  getModuleResources,
} from "../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} KB`;
}

export default function HomeworkResourcesBlock({ slug }: { slug: string }) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { data } = useQuery<ModuleResources | null>({
    queryKey: ["resources", slug],
    queryFn: () => getModuleResources(slug),
    retry: false,
  });
  const [busy, setBusy] = useState<string | null>(null);

  if (!data || data.files.length === 0) return null;

  const download = async (name: string) => {
    setBusy(name);
    try {
      await downloadModuleResource(slug, name);
    } catch {
      /* surfaced by the disabled→enabled toggle; non-fatal */
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      sx={{
        my: 4,
        p: 2.5,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
        <Inventory2Icon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.primary.main }}
        >
          {data.title || t("HomeworkResourcesBlock.title")}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        {data.files.map((f) => (
          <Stack
            key={f.name}
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{
              p: 1,
              borderRadius: 1.5,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={
                busy === f.name ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={busy === f.name}
              onClick={() => download(f.name)}
              sx={{
                textTransform: "none",
                fontFamily: MONO,
                fontSize: "0.76rem",
                borderColor: alpha(theme.palette.primary.main, 0.4),
                color: theme.palette.primary.main,
                minWidth: 200,
                justifyContent: "flex-start",
                "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.06) },
              }}
            >
              {f.name}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {f.label}
              {f.bytes ? (
                <Typography component="span" variant="caption" color="text.disabled">
                  {"  ·  "}
                  {fmtBytes(f.bytes)}
                </Typography>
              ) : null}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
